import React from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import {
  MapPin,
  Lock,
  LogOut,
  Users,
  Mail,
  Calendar,
  Download,
  RefreshCw,
  ArrowLeft,
  Search,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { SpotsTab, ReportsTab, DevicesTab, ReferralsTab, CampaignTab, ParticipantsTab, FunnelTab, NotificationsTab, HistoryTab } from "../components/AdminTabs";
import { ParkingDataTab, AnalyticsTab } from "../components/AdminMapTabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "spotdrop_admin_key";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Malta",
    });
  } catch {
    return iso;
  }
};

/** Server-driven CSV export: hits GET /api/waitlist/export.csv with the admin
 *  key and forces a browser download. Preferred over client-side CSV building
 *  because it returns the FULL waitlist (up to 50 000 rows), handles edge-case
 *  characters via Python's csv module, and includes the row `id` for de-dup. */
const downloadServerCSV = async (adminKey) => {
  try {
    const res = await axios.get(`${API}/waitlist/export.csv`, {
      headers: { "X-Admin-Key": adminKey },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    // Prefer the server-supplied filename; fall back to today's date.
    const dispo = res.headers?.["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/.exec(dispo);
    a.download = match ? match[1] : `spotdrop-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded.");
  } catch (err) {
    if (err?.response?.status === 401) toast.error("Session expired. Please log in again.");
    else toast.error("Could not export CSV.");
  }
};

const LoginCard = ({ onLogin }) => {
  const [key, setKey] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim()) {
      toast.error("Please enter the admin key");
      return;
    }
    setLoading(true);
    try {
      await axios.get(`${API}/waitlist`, {
        headers: { "X-Admin-Key": key.trim() },
      });
      localStorage.setItem(STORAGE_KEY, key.trim());
      toast.success("Welcome back");
      onLogin(key.trim());
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin key");
      } else {
        toast.error("Could not connect. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[#0a0a0c]">
      <form
        onSubmit={submit}
        className="w-full max-w-md p-6 md:p-8 rounded-2xl bg-[#141416] border border-white/10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-[#ccff00] flex items-center justify-center">
            <Lock className="w-4 h-4 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold">Admin access</div>
            <div className="text-xs text-zinc-500">SpotDrop Malta · Waitlist</div>
          </div>
        </div>

        <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
          Admin key
        </label>
        <Input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Enter your admin key"
          className="mt-2 h-12 bg-[#0a0a0c] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#ccff00] focus-visible:ring-1 focus-visible:border-[#ccff00] rounded-xl"
          data-testid="admin-key-input"
        />

        <Button
          type="submit"
          disabled={loading}
          className="w-full mt-5 h-12 bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold rounded-xl"
          data-testid="admin-login-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            "Unlock"
          )}
        </Button>

        <a
          href="/"
          className="mt-5 inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Back to homepage
        </a>
      </form>
      <Toaster theme="dark" position="bottom-center" />
    </div>
  );
};

const Dashboard = ({ adminKey, onLogout }) => {
  const [tab, setTab] = React.useState("waitlist");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/waitlist`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setItems(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load waitlist");
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, onLogout]);

  React.useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.name?.toLowerCase().includes(q) ||
        i.email?.toLowerCase().includes(q) ||
        i.area?.toLowerCase().includes(q)
    );
  }, [items, query]);

  const areaStats = React.useMemo(() => {
    const m = {};
    for (const i of items) {
      const a = (i.area || "").trim() || "Unknown";
      m[a] = (m[a] || 0) + 1;
    }
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [items]);

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white">
      <header className="border-b border-white/5 bg-[#0a0a0c]/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="admin-logo">
            <div className="w-8 h-8 rounded-md bg-[#ccff00] flex items-center justify-center">
              <MapPin className="w-4 h-4 text-[#0a0a0c]" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-semibold tracking-tight text-sm md:text-base">
              SpotDrop<span className="text-[#ccff00]">.</span>{" "}
              <span className="text-zinc-500 font-normal">admin</span>
            </span>
          </a>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchAll}
              variant="ghost"
              size="sm"
              className="h-9 text-zinc-300 hover:text-white hover:bg-white/5"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline ml-2">Refresh</span>
            </Button>
            <Button
              onClick={() => downloadServerCSV(adminKey)}
              variant="ghost"
              size="sm"
              className="h-9 text-zinc-300 hover:text-white hover:bg-white/5"
              data-testid="admin-export-button"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">CSV</span>
            </Button>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="h-9 text-zinc-400 hover:text-white hover:bg-white/5"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
              Dashboard
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mt-2">
              {tab === "waitlist" ? "Waitlist" : tab === "spots" ? "Live spots" : tab === "history" ? "Spot history" : tab === "reports" ? "Reports" : tab === "devices" ? "Devices" : tab === "referrals" ? "Referrals" : tab === "campaign" ? "Campaign" : tab === "funnel" ? "Activation funnel" : tab === "notifications" ? "Notifications" : tab === "parking" ? "Parking data" : tab === "analytics" ? "Analytics" : "Participants"}
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-[#141416] border border-white/10 mb-8 w-fit" data-testid="admin-tabs">
          {[
            { k: "waitlist", label: "Waitlist" },
            { k: "spots", label: "Spots" },
            { k: "history", label: "History" },
            { k: "reports", label: "Reports" },
            { k: "devices", label: "Devices" },
            { k: "referrals", label: "Referrals" },
            { k: "campaign", label: "Campaign" },
            { k: "participants", label: "Participants" },
            { k: "funnel", label: "Funnel" },
            { k: "notifications", label: "Notifications" },
            { k: "parking", label: "Parking data" },
            { k: "analytics", label: "Analytics" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              className={`px-3 md:px-4 h-9 rounded-lg text-sm font-medium transition-colors ${
                tab === t.k ? "bg-[#ccff00] text-[#0a0a0c]" : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "spots" && <SpotsTab adminKey={adminKey} />}
        {tab === "history" && <HistoryTab adminKey={adminKey} />}
        {tab === "reports" && <ReportsTab adminKey={adminKey} />}
        {tab === "devices" && <DevicesTab adminKey={adminKey} />}
        {tab === "referrals" && <ReferralsTab adminKey={adminKey} />}
        {tab === "campaign" && <CampaignTab adminKey={adminKey} />}
        {tab === "participants" && <ParticipantsTab adminKey={adminKey} />}
        {tab === "funnel" && <FunnelTab adminKey={adminKey} />}
        {tab === "notifications" && <NotificationsTab adminKey={adminKey} />}
        {tab === "parking" && <ParkingDataTab adminKey={adminKey} />}
        {tab === "analytics" && <AnalyticsTab adminKey={adminKey} />}

        {tab === "waitlist" && (
        <>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="p-5 rounded-2xl bg-[#141416] border border-white/10" data-testid="stat-total">
            <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.18em]">
              <Users className="w-3.5 h-3.5" /> Total signups
            </div>
            <div className="mt-3 font-heading text-3xl md:text-4xl font-bold">
              {items.length}
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-[#141416] border border-white/10">
            <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.18em]">
              <Calendar className="w-3.5 h-3.5" /> Latest
            </div>
            <div className="mt-3 font-heading text-base md:text-lg font-medium truncate">
              {items[0] ? formatDate(items[0].created_at) : "—"}
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-[#141416] border border-white/10 col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.18em]">
              <MapPin className="w-3.5 h-3.5" /> Top areas
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {areaStats.length === 0 ? (
                <span className="text-zinc-600 text-sm">—</span>
              ) : (
                areaStats.map(([a, n]) => (
                  <span
                    key={a}
                    className="text-xs px-2 py-1 rounded-md bg-[#ccff00]/10 border border-[#ccff00]/20 text-[#ccff00]"
                  >
                    {a} · {n}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or area..."
            className="pl-11 h-12 bg-[#141416] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#ccff00] focus-visible:ring-1 focus-visible:border-[#ccff00] rounded-xl"
            data-testid="admin-search-input"
          />
        </div>

        {/* Table / List */}
        <div className="rounded-2xl bg-[#141416] border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="admin-empty">
              {items.length === 0
                ? "No signups yet. Share your link!"
                : "No results match your search."}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.18em] text-zinc-500 border-b border-white/5">
                      <th className="py-4 px-6 font-semibold">Name</th>
                      <th className="py-4 px-6 font-semibold">Email</th>
                      <th className="py-4 px-6 font-semibold">Area</th>
                      <th className="py-4 px-6 font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody data-testid="admin-table">
                    {filtered.map((it) => (
                      <tr
                        key={it.id}
                        className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="py-4 px-6 text-white font-medium">{it.name}</td>
                        <td className="py-4 px-6 text-zinc-300">
                          <a
                            href={`mailto:${it.email}`}
                            className="hover:text-[#ccff00] transition-colors"
                          >
                            {it.email}
                          </a>
                        </td>
                        <td className="py-4 px-6 text-zinc-400">{it.area}</td>
                        <td className="py-4 px-6 text-zinc-500 text-xs">
                          {formatDate(it.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-white/5">
                {filtered.map((it) => (
                  <div key={it.id} className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-white truncate">{it.name}</div>
                        <a
                          href={`mailto:${it.email}`}
                          className="text-sm text-zinc-400 hover:text-[#ccff00] truncate block"
                        >
                          {it.email}
                        </a>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-md bg-[#ccff00]/10 border border-[#ccff00]/20 text-[#ccff00] whitespace-nowrap">
                        {it.area}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-zinc-500">
                      {formatDate(it.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <p className="text-xs text-zinc-600 mt-6 flex items-center gap-2">
          <Mail className="w-3 h-3" /> Tip: Click any email to compose a message.
        </p>
        </>
        )}
      </main>

      <Toaster theme="dark" position="bottom-center" />
    </div>
  );
};

export default function Admin() {
  const [adminKey, setAdminKey] = React.useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null
  );

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAdminKey(null);
  };

  if (!adminKey) {
    return <LoginCard onLogin={setAdminKey} />;
  }

  return <Dashboard adminKey={adminKey} onLogout={logout} />;
}
